import { type FC } from 'react'
import { Form } from 'react-bootstrap'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Modal } from '../../../components/Modal/Modal'
import { TimezoneSelector } from '../../../components/TimezoneSelector/TimezoneSelector'
import { type Location } from '../../../models/Scheduling'
import { type LocationInput } from '../../../store/scheduling/dto'

interface LocationModalProps {
  location?: Location
  onCancel: () => void
  onSave: (input: LocationInput) => void
}

const locationSchema = z.object({
  name: z.string().trim().min(1),
  address: z.string().trim().min(1),
  note: z.string().optional(),
  timeZone: z.string().optional(),
})

type LocationFormValues = z.infer<typeof locationSchema>

export const LocationModal: FC<LocationModalProps> = ({ location, onCancel, onSave }) => {
  const { control, register, handleSubmit } = useForm<LocationFormValues>({
    resolver: zodResolver(locationSchema),
    mode: 'onChange',
    defaultValues: { name: location?.name ?? '', address: location?.address ?? '', note: location?.note ?? '', timeZone: location?.timeZone ?? '' },
  })
  const name = useWatch({ control, name: 'name' })
  const address = useWatch({ control, name: 'address' })

  const onSubmit = (values: LocationFormValues) => {
    onSave({ name: values.name.trim(), address: values.address.trim(), note: values.note?.trim(), timeZone: values.timeZone || undefined })
  }

  return (
    <Modal
      show
      onHide={onCancel}
      header={
        <div className='d-flex flex-column zocks-gap-0.25'>
          <h5 className='mb-0'>{location ? 'Edit location' : 'Add location'}</h5>
          <span className='paragraph-small zocks-text-gray-600'>Add a place clients can meet you in person.</span>
        </div>
      }
      headerBodySeparator
      body={
        <div className='d-flex flex-column gap-3'>
          <div className='d-flex flex-column zocks-gap-0.25'>
            <label className='bold' htmlFor='location-name'>
              Name
            </label>
            <Form.Control id='location-name' type='text' placeholder='e.g. Main Office' {...register('name')} />
          </div>
          <div className='d-flex flex-column zocks-gap-0.25'>
            <label className='bold' htmlFor='location-address'>
              Address
            </label>
            <Form.Control id='location-address' type='text' placeholder='Street, city, state ZIP' {...register('address')} />
          </div>
          <div className='d-flex flex-column zocks-gap-0.25'>
            <label className='bold' htmlFor='location-note'>
              Note <span className='zocks-text-gray-500'>(optional)</span>
            </label>
            <Form.Control id='location-note' type='text' placeholder='Suite or floor number' {...register('note')} />
          </div>
          <div className='d-flex flex-column zocks-gap-0.25'>
            <label className='bold'>Timezone</label>
            <Controller
              control={control}
              name='timeZone'
              render={({ field }) => (
                <TimezoneSelector initialSelectedTimezone={field.value ?? ''} onTimezoneSelected={(value) => field.onChange(value ?? '')} />
              )}
            />
          </div>
        </div>
      }
      showFooterActions
      footerPrimaryActionTitle='Save location'
      footerSecondaryActionTitle='Cancel'
      footerPrimaryActionDisabled={!name?.trim() || !address?.trim()}
      onFooterPrimaryAction={handleSubmit(onSubmit)}
      onFooterSecondaryAction={onCancel}
    />
  )
}
